import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Custom hook to track user idle time and trigger auto logout.
 *
 * Uses a timestamp-based approach: stores the last activity time and checks
 * elapsed time, rather than relying solely on setTimeout (which can drift
 * when tabs are throttled or sleeping).
 *
 * @param {number} timeoutMinutes - Minutes of inactivity before logout (default: 15)
 * @param {number} warningMinutes - Minutes before timeout to show warning (default: 1)
 * @param {function} onTimeout - Callback function to execute on timeout (logout)
 * @param {boolean} enabled - Whether the idle timer is active (false when not logged in)
 * @returns {object} - { showWarning, remainingSeconds, dismissWarning }
 */
const useIdleTimeout = (timeoutMinutes = 15, warningMinutes = 1, onTimeout, enabled = true) => {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  // Refs to avoid stale closures and unnecessary effect re-runs
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;
  const timeoutMinutesRef = useRef(timeoutMinutes);
  timeoutMinutesRef.current = timeoutMinutes;
  const warningMinutesRef = useRef(warningMinutes);
  warningMinutesRef.current = warningMinutes;

  // Core state stored in ref — not in React state to avoid re-render loops
  const lastActivityRef = useRef(Date.now());
  const tickIntervalRef = useRef(null);
  const hasLoggedOutRef = useRef(false);

  // Record user activity — just update the timestamp
  const recordActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Dismiss warning and record activity
  const dismissWarning = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);
    setRemainingSeconds(0);
  }, []);

  useEffect(() => {
    if (!enabled) {
      // Not logged in — clear everything
      if (tickIntervalRef.current) {
        clearInterval(tickIntervalRef.current);
        tickIntervalRef.current = null;
      }
      setShowWarning(false);
      setRemainingSeconds(0);
      hasLoggedOutRef.current = false;
      return;
    }

    // Reset state on enable
    lastActivityRef.current = Date.now();
    hasLoggedOutRef.current = false;

    // --- Tick every 5 seconds to check elapsed idle time ---
    // This is resilient to tab sleep/throttling because it checks
    // real elapsed time, not just setTimeout duration.
    tickIntervalRef.current = setInterval(() => {
      if (hasLoggedOutRef.current) return;

      const now = Date.now();
      const elapsedMs = now - lastActivityRef.current;
      const timeoutMs = timeoutMinutesRef.current * 60 * 1000;
      const warningMs = (timeoutMinutesRef.current - warningMinutesRef.current) * 60 * 1000;

      if (elapsedMs >= timeoutMs) {
        // Session expired
        hasLoggedOutRef.current = true;
        clearInterval(tickIntervalRef.current);
        tickIntervalRef.current = null;
        setShowWarning(false);
        setRemainingSeconds(0);
        if (onTimeoutRef.current) {
          onTimeoutRef.current();
        }
      } else if (elapsedMs >= warningMs) {
        // In warning zone — show countdown
        const secsLeft = Math.ceil((timeoutMs - elapsedMs) / 1000);
        setShowWarning(true);
        setRemainingSeconds(secsLeft);
      } else {
        // Active — hide warning if it was showing
        setShowWarning(false);
        setRemainingSeconds(0);
      }
    }, 5000); // Check every 5 seconds

    // --- Activity event listeners ---
    // Using keydown instead of keypress (keypress is deprecated and
    // doesn't fire for Backspace, Delete, Arrow keys, Tab, etc.)
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click', 'input'];

    // Debounce to avoid excessive timestamp updates
    let debounceTimer = null;
    const handleActivity = () => {
      if (hasLoggedOutRef.current) return;
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        lastActivityRef.current = Date.now();
      }, 500);
    };

    // When tab becomes visible, do an immediate idle check
    const handleVisibilityChange = () => {
      if (hasLoggedOutRef.current) return;
      if (document.visibilityState === 'visible') {
        const elapsedMs = Date.now() - lastActivityRef.current;
        const timeoutMs = timeoutMinutesRef.current * 60 * 1000;
        if (elapsedMs >= timeoutMs) {
          hasLoggedOutRef.current = true;
          clearInterval(tickIntervalRef.current);
          tickIntervalRef.current = null;
          setShowWarning(false);
          if (onTimeoutRef.current) {
            onTimeoutRef.current();
          }
        }
      }
    };

    events.forEach(event => window.addEventListener(event, handleActivity, { passive: true }));
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      events.forEach(event => window.removeEventListener(event, handleActivity));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (tickIntervalRef.current) {
        clearInterval(tickIntervalRef.current);
        tickIntervalRef.current = null;
      }
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [enabled]); // Only re-run when enabled changes (login/logout)

  return {
    showWarning,
    remainingSeconds,
    dismissWarning
  };
};

export default useIdleTimeout;
